import React, { useState, useRef, useEffect } from 'react';
import ProgrammaticShape from './ProgrammaticShape';
import './DraggableComponent.css';

// VERSION: Icon Image Support - v2024-12-10
const DraggableComponent = ({ component, isSelected, onDrag, onSelect, onAction, onPress, isDraggable = true, disableAnimations = false, disableLongPress = false, disableResize = false, canvasSize = { width: 800, height: 600 }, accessibilityOptions = {}, onComponentInit }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [toggleState, setToggleState] = useState(component.toggleState?.currentState || 'off');
  const componentRef = useRef(null);
  
  // Debug: Log component details on render - ICON IMAGE VERSION
  console.log('🔧 DraggableComponent render [ICON IMAGE v2024-12-10]:', {
    id: component.id,
    type: component.type,
    iconType: component.content?.iconType,
    hasIconImage: !!component.content?.iconImage,
    iconOffsetX: component.content?.iconOffsetX,
    iconOffsetY: component.content?.iconOffsetY,
    behaviorType: component.behavior?.behaviorType,
    isToggleable: component.toggleState?.isToggleable,
    states: component.toggleState?.states,
    currentState: component.toggleState?.currentState,
    localToggleState: toggleState
  });
  const longPressTimerRef = useRef(null);
  const longPressCompletedRef = useRef(false);
  const pressStartTimeRef = useRef(null); // Track when press started
  const dragStateRef = useRef({
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    hasDragged: false
  });
  const resizeStateRef = useRef({
    isResizing: false,
    resizeHandle: '',
    startSize: { width: 0, height: 0 },
    startPosition: { x: 0, y: 0 },
    startMouse: { x: 0, y: 0 }
  });

  // Helper functions to get current display content based on toggle state
  const getCurrentDisplayContent = () => {
    if (!component.toggleState?.isToggleable || !component.toggleState?.states) {
      return {
        text: component.content?.primaryText || '',
        icon: component.content?.icon
      };
    }

    const currentStateData = component.toggleState.states[toggleState];
    return {
      text: currentStateData?.name || component.content?.primaryText || '',
      icon: currentStateData?.icon || component.content?.icon
    };
  };

  const handleToggle = () => {
    console.log('🔧 handleToggle called. Component toggle state:', component.toggleState);
    console.log('🔧 Current toggleState:', toggleState);
    console.log('🔧 isToggleable?', component.toggleState?.isToggleable);
    console.log('🔧 has states?', !!component.toggleState?.states);
    
    if (component.toggleState?.isToggleable && component.toggleState?.states) {
      const newState = toggleState === 'off' ? 'on' : 'off';
      setToggleState(newState);
      
      // Update component data for next action
      const updatedComponent = {
        ...component,
        toggleState: {
          ...component.toggleState,
          currentState: newState
        }
      };
      
      console.log(`🔄 Toggle state changed: ${toggleState} → ${newState}`);
      return updatedComponent;
    } else {
      console.log('❌ Toggle conditions not met');
    }
    return component;
  };

  const handleMouseMove = (e) => {
    // Handle resize
    if (resizeStateRef.current.isResizing) {
      console.log('Resizing component');
      const deltaX = e.clientX - resizeStateRef.current.startMouse.x;
      const deltaY = e.clientY - resizeStateRef.current.startMouse.y;
      
      let newSize = { ...resizeStateRef.current.startSize };
      let newPosition = { ...resizeStateRef.current.startPosition };
      
      const handle = resizeStateRef.current.resizeHandle;
      
      // Apply resize based on handle type
      if (handle.includes('e')) { // East (right)
        newSize.width = Math.max(50, resizeStateRef.current.startSize.width + deltaX);
      }
      if (handle.includes('w')) { // West (left)
        const widthChange = deltaX;
        newSize.width = Math.max(50, resizeStateRef.current.startSize.width - widthChange);
        newPosition.x = resizeStateRef.current.startPosition.x + (resizeStateRef.current.startSize.width - newSize.width);
      }
      if (handle.includes('s')) { // South (bottom)
        newSize.height = Math.max(30, resizeStateRef.current.startSize.height + deltaY);
      }
      if (handle.includes('n')) { // North (top)
        const heightChange = deltaY;
        newSize.height = Math.max(30, resizeStateRef.current.startSize.height - heightChange);
        newPosition.y = resizeStateRef.current.startPosition.y + (resizeStateRef.current.startSize.height - newSize.height);
      }
      
      // Update component with new size and position
      onDrag(component.id, { 
        size: newSize,
        position: newPosition
      });

      // Update properties panel with new shape size

      return;
    }
    
    // Handle drag
    console.log('Mouse move - checking drag state:', dragStateRef.current.isDragging);
    if (!dragStateRef.current.isDragging) return;
    
    console.log('Mouse move - dragging confirmed');
    dragStateRef.current.hasDragged = true;
    setHasDragged(true);
    
    const newPosition = {
      x: e.clientX - dragStateRef.current.dragStart.x,
      y: e.clientY - dragStateRef.current.dragStart.y
    };
    
    console.log('New position:', newPosition);
    
    // Ensure component stays within canvas bounds (with some padding)
    const canvasWidth = canvasSize.width;
    const canvasHeight = canvasSize.height;
    newPosition.x = Math.max(0, Math.min(newPosition.x, canvasWidth - component.size.width));
    newPosition.y = Math.max(0, Math.min(newPosition.y, canvasHeight - component.size.height));

    const newSize = { ...component.size };
    
    // Update the component position immediately
    onDrag(component.id, {
      size: newSize,         // { width: ..., height: ... }
      position: newPosition  // { x: ..., y: ... }
    });
  };

  const handleMouseUp = () => {
    console.log('Mouse up - ending drag/resize');
    
    // End resize
    if (resizeStateRef.current.isResizing) {
      resizeStateRef.current.isResizing = false;
      setIsResizing(false);
    }
    
    // End drag
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
    
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    // Only trigger selection if we didn't drag AND it was a long press
    if (!dragStateRef.current.hasDragged && !resizeStateRef.current.isResizing && longPressCompletedRef.current) {
      console.log('handleMouseUp: Calling onSelect for long press');
      onSelect();
      console.log('Long press selection completed');
    } else {
      console.log('handleMouseUp: NOT calling onSelect - hasDragged:', dragStateRef.current.hasDragged, 'isResizing:', resizeStateRef.current.isResizing, 'longPressCompleted:', longPressCompletedRef.current);
    }
    
    // Reset drag flags after a short delay to allow future clicks
    setTimeout(() => {
      console.log('Resetting drag flags for future clicks');
      dragStateRef.current.hasDragged = false;
      setHasDragged(false);
    }, 200); // Small delay to ensure click events are processed first
  };

  const handleMouseDown = (e) => {
    console.log('Mouse down on component:', component.id, 'isSelected:', isSelected, 'isDraggable:', isDraggable);
    e.stopPropagation();
    
    // Skip long press behavior when not draggable (vehicle mode)
    if (!isDraggable) {
      console.log('Component not draggable - skipping long press selection behavior');
      return;
    }
    
    // Reset long press flags at start of new interaction
    longPressCompletedRef.current = false;
    
    // Only setup long press if not disabled (for mosaic mode)
    if (!disableLongPress) {
      // Record when the press started
      pressStartTimeRef.current = Date.now();
      setIsLongPressing(true);
      
      // Add mouse up listener to cancel long press if released early
      const handleEarlyMouseUp = () => {
        console.log('Mouse released early - canceling long press timer');
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        setIsLongPressing(false);
        document.removeEventListener('mouseup', handleEarlyMouseUp);
        document.removeEventListener('mouseleave', handleEarlyMouseUp);
      };
      
      document.addEventListener('mouseup', handleEarlyMouseUp);
      document.addEventListener('mouseleave', handleEarlyMouseUp);
      
      // Set timer for long press (600ms)
      longPressTimerRef.current = setTimeout(() => {
        console.log('Long press detected - selecting component');
        longPressCompletedRef.current = true;
        setIsLongPressing(false);
        // Remove the early mouse up listeners since we're done
        document.removeEventListener('mouseup', handleEarlyMouseUp);
        document.removeEventListener('mouseleave', handleEarlyMouseUp);
        onSelect();
        console.log('Long press timer selection completed');
      }, 600);
    } else {
      // Even when long press is disabled, show brief press animation for feedback
      setIsLongPressing(true);
      setTimeout(() => {
        setIsLongPressing(false);
      }, 150); // Quick 150ms press animation
    }
    
    // If component is not selected, don't start dragging yet
    if (!isSelected) {
      return;
    }
    
    // Component is selected - allow normal drag behavior
    dragStateRef.current.hasDragged = false;
    dragStateRef.current.isDragging = true;
    dragStateRef.current.dragStart = {
      x: e.clientX - component.position.x,
      y: e.clientY - component.position.y
    };
    
    setHasDragged(false);
    setIsDragging(true);
    
    console.log('Adding drag event listeners, drag start:', dragStateRef.current.dragStart);
    
    // Add global mouse events for dragging
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle immediate clicks for actions (navigation, etc.)
  const handleClick = (e) => {
    console.log('Click event on component:', component.id, 'longPressCompleted:', longPressCompletedRef.current, 'isLongPressing:', isLongPressing, 'hasDragged:', hasDragged);
    e.stopPropagation();
    
    // Clear any pending long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // For mosaic mode (when not draggable), show immediate press animation
    if (!isDraggable) {
      setIsLongPressing(true);
      setTimeout(() => {
        setIsLongPressing(false);
      }, 150);
    } else {
      setIsLongPressing(false);
    }
    
    // Calculate how long the press was
    const pressDuration = pressStartTimeRef.current ? Date.now() - pressStartTimeRef.current : 0;
    console.log('Press duration:', pressDuration, 'ms');
    
    // If it was a long press (≥600ms) or long press already completed, don't trigger click action
    if (pressDuration >= 600 || longPressCompletedRef.current) {
      console.log('Long press detected, blocking click action');
      // Reset flags
      setTimeout(() => {
        longPressCompletedRef.current = false;
        pressStartTimeRef.current = null;
      }, 100);
      return;
    }
    
    // If the component was just dragged, prevent the click action
    if (hasDragged || dragStateRef.current.hasDragged) {
      console.log('Component was just dragged, blocking click action');
      return;
    }
    
    // If component is selected (in edit mode), don't trigger functional actions
    if (isSelected) {
      console.log('Component is selected (edit mode), blocking functional actions');
      return;
    }
    
    // Short press: use onPress if available, otherwise fall back to onAction
    if (onPress) {
      console.log('Short press detected - triggering onPress handler');
      
      // Handle toggle state change if applicable
      const updatedComponent = handleToggle();
      
      onPress(updatedComponent.id, e, updatedComponent);
      // Reset timing
      pressStartTimeRef.current = null;
      return;
    } else if (component.behavior?.action && onAction) {
      console.log('Short press detected - triggering action:', component.behavior.action, 'Target:', component.behavior.targetPage);
      
      // Handle toggle state change if applicable
      const updatedComponent = handleToggle();
      
      onAction(updatedComponent.id, e, updatedComponent);
      // Reset timing
      pressStartTimeRef.current = null;
      return;
    }
    
    // If no action, treat as selection attempt (but this should have been handled by long press)
    console.log('No action defined for component');
    pressStartTimeRef.current = null;
  };

  const handleResizeMouseDown = (e, handle) => {
    console.log('🎯 Resize handle mouse down detected:', handle);
    e.stopPropagation();
    e.preventDefault();
    
    resizeStateRef.current.isResizing = true;
    resizeStateRef.current.resizeHandle = handle;
    resizeStateRef.current.startSize = { ...component.size };
    resizeStateRef.current.startPosition = { ...component.position };
    resizeStateRef.current.startMouse = { x: e.clientX, y: e.clientY };
    
    setIsResizing(true);
    
    // Add global mouse events
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Cleanup event listeners and timers on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Get shape-specific styles
  const getShapeStyles = (shape) => {
    const width = component.size.width;
    const height = component.size.height;
    
    switch (shape) {
      case 'square':
        return { borderRadius: '0px' };
      case 'circle':
        return { borderRadius: '50%' };
      case 'pill':
        return { borderRadius: `${Math.min(width, height) / 2}px` };
      case 'hexagon':
        return {
          borderRadius: '0px',
          clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
        };
      case 'diamond':
        return {
          borderRadius: '0px',
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
        };
      case 'rounded':
      default:
        return { borderRadius: '8px' };
    }
  };

  const shapeStyles = getShapeStyles(component.style?.shape || 'rounded');

  // Handle background styling based on type
  const getBackgroundStyles = () => {
    const backgroundType = component.style?.backgroundType || 'solid';
    
    if (backgroundType === 'image' && component.style?.backgroundImage) {
      // When using img element, don't use CSS background-image
      return {
        backgroundColor: 'transparent',
        backgroundImage: 'none'
      };
    } else {
      return {
        backgroundColor: component.style?.backgroundColor || '#007AFF',
        backgroundImage: 'none'
      };
    }
  };

  const backgroundStyles = getBackgroundStyles();

  // Create dynamic border styles
  const getBorderStyles = () => {
    // Selection and interaction states take priority
    if (isSelected) {
      return '2px solid #FF6B35';
    }
    if (isLongPressing) {
      return '2px solid #FFD700';
    }
    
    // Use custom border if enabled
    if (component.style?.borderEnabled) {
      const borderWidth = component.style?.borderWidth || '1';
      const borderColor = component.style?.borderColor || '#cccccc';
      return `${borderWidth}px solid ${borderColor}`;
    }
    
    // No border when not enabled
    return 'none';
  };

  // Determine flex direction based on icon position
  const getFlexDirection = () => {
    const iconPosition = component.content?.iconPosition || 'top';
    switch (iconPosition) {
      case 'left': return 'row';
      case 'right': return 'row-reverse';
      case 'bottom': return 'column-reverse';
      case 'top':
      default: return 'column';
    }
  };

  const componentStyle = {
    position: 'absolute',
    left: `${component.position.x}px`,
    top: `${component.position.y}px`,
    width: `${component.size.width}px`,
    height: `${component.size.height}px`,
    ...backgroundStyles, // Apply background styles (color or image)
    color: component.style?.textColor || '#FFFFFF',
    fontSize: `${component.content?.fontSize || 16}px`,
    fontFamily: component.content?.fontFamily || 'Arial',
    border: getBorderStyles(),
    ...shapeStyles, // Apply shape-specific styles
    display: 'flex',
    flexDirection: getFlexDirection(),
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isResizing ? 'nw-resize' : (isDragging ? 'grabbing' : (isSelected ? 'grab' : 'pointer')),
    userSelect: 'none',
    boxShadow: disableAnimations ? '0 2px 8px rgba(0,0,0,0.1)' : ((isDragging || isResizing) ? '0 8px 16px rgba(0,0,0,0.3)' : (isLongPressing ? '0 4px 12px rgba(255,215,0,0.5)' : '0 2px 8px rgba(0,0,0,0.1)')),
    transform: disableAnimations ? 'scale(1)' : ((isDragging || isResizing) ? 'scale(1.02)' : (isLongPressing ? 'scale(1.01)' : 'scale(1)')),
    transition: disableAnimations ? 'none !important' : ((isDragging || isResizing) ? 'none' : (isLongPressing ? 'all 0.1s ease' : 'all 0.2s ease')),
    zIndex: (isDragging || isResizing) ? 1000 : isSelected ? 100 : (component.zIndex || 1),
    opacity: disableAnimations ? 1 : (isLongPressing ? 0.9 : 1)
  };

  console.log('DraggableComponent rendered:', component.id);

  return (
    <div
      ref={componentRef}
      className={`canvas-component ${component.type} ${isSelected ? 'selected' : ''}`}
      style={componentStyle}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      tabIndex={component.tabIndex ?? -1}
      role="button"
      aria-label={component.accessibility?.ariaLabel || getCurrentDisplayContent().text || 'Draggable component'}
      onKeyDown={e => {
        if (e.key === ' ') {
          // Spacebar: trigger long press selection
          if (!isSelected && isDraggable && !disableLongPress) {
            onSelect();
          }
        }
        if (e.key === 'Enter') {
          // Enter: trigger button action (short press)
          if (component.behavior?.action && onAction) {
            const updatedComponent = handleToggle();
            onAction(component.id, e, updatedComponent);
          } else if (onPress) {
            const updatedComponent = handleToggle();
            onPress(component.id, e, updatedComponent);
          }
        }
        if (e.key === 'Escape') {
          // Escape: unselect the object
          if (isSelected && typeof onSelect === 'function') {
            console.log('Escape key pressed - deselecting component');
            onSelect(null); // Or your deselect logic
            console.log('Component deselected via Escape key');
          }
        }
        // Future feature: arrow keys for moving
        // if (e.key === 'ArrowUp') { ... }
      }}
    >
      {/* Use programmatic shape when vector scaling is enabled */}
      {accessibilityOptions.vectorScaling ? (
        <ProgrammaticShape
          type={component.type}
          width={component.size?.width || 100}
          height={component.size?.height || 40}
          style={{
            backgroundColor: component.style?.backgroundColor || '#007AFF',
            borderColor: component.style?.borderColor,
            borderWidth: component.style?.borderWidth,
            borderRadius: component.style?.borderRadius,
            fontSize: component.style?.fontSize,
            fontFamily: component.style?.fontFamily,
            color: component.style?.color,
            activeColor: component.style?.activeColor,
            inactiveColor: component.style?.inactiveColor,
            thumbColor: component.style?.thumbColor,
            trackColor: component.style?.trackColor
          }}
          content={{
            primaryText: getCurrentDisplayContent().text,
            icon: getCurrentDisplayContent().icon,
            secondaryText: component.content?.secondaryText,
            value: component.content?.value // For sliders
          }}
          state={
            component.toggleState?.isToggleable 
              ? toggleState 
              : (isLongPressing ? 'active' : 'default')
          }
        />
      ) : (
        // Traditional rendering with icon and text
        <>
          {/* Background Image as img element for better scaling */}
          {component.style?.backgroundType === 'image' && component.style?.backgroundImage && (
            <img
              src={component.style.backgroundImage}
              alt=""
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain', // Maintain aspect ratio, fit within bounds
                objectPosition: 'center',
                imageRendering: 'auto', // Let browser choose best quality
                pointerEvents: 'none',
                userSelect: 'none',
                zIndex: -1 // Behind content
              }}
            />
          )}

          {/* Render icon as image or emoji based on iconType */}
          {component.content?.iconType === 'image' && component.content?.iconImage ? (
            // Image icons use direct img tag to maintain exact dimensions
            <img
              src={component.content.iconImage}
              alt={component.content?.iconImageName || 'Icon'}
              style={{
                width: `${component.content?.iconWidth || 32}px`,
                height: `${component.content?.iconHeight || 32}px`,
                objectFit: component.content?.iconFit || 'fill',
                objectPosition: 'center',
                imageRendering: 'auto',
                pointerEvents: 'none',
                userSelect: 'none',
                position: 'relative',
                zIndex: 1,
                flexShrink: 0,
                transform: `translate(${component.content?.iconOffsetX || 0}px, ${component.content?.iconOffsetY || 0}px)`,
                ...(component.content?.iconPosition === 'top' && { marginBottom: '4px' }),
                ...(component.content?.iconPosition === 'bottom' && { marginTop: '4px' }),
                ...(component.content?.iconPosition === 'left' && { marginRight: '8px' }),
                ...(component.content?.iconPosition === 'right' && { marginLeft: '8px' })
              }}
            />
          ) : component.content?.icon ? (
            // Emoji icons use container div with size and offset controls
            <div 
              className="component-icon" 
              style={{ 
                fontSize: `${component.content?.iconWidth || 32}px`, 
                width: `${component.content?.iconWidth || 32}px`,
                height: `${component.content?.iconHeight || 32}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: `translate(${component.content?.iconOffsetX || 0}px, ${component.content?.iconOffsetY || 0}px)`,
                marginBottom: component.content?.iconPosition === 'top' ? '4px' : '0',
                marginTop: component.content?.iconPosition === 'bottom' ? '4px' : '0',
                marginLeft: component.content?.iconPosition === 'right' ? '8px' : '0',
                marginRight: component.content?.iconPosition === 'left' ? '8px' : '0',
                pointerEvents: 'none',
                userSelect: 'none',
                position: 'relative',
                zIndex: 1,
                flexShrink: 0
              }}
            >
              {getCurrentDisplayContent().icon}
            </div>
          ) : null}
          
          <div 
            className="component-text"
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
              position: 'relative',
              zIndex: 1
            }}
          >
            {getCurrentDisplayContent().text}
          </div>
          
          {component.content?.secondaryText && (
            <div 
              className="component-subtext" 
              style={{ 
                fontSize: '12px', 
                opacity: 0.8,
                pointerEvents: 'none',
                userSelect: 'none',
                position: 'relative',
                zIndex: 1
              }}
            >
              {component.content.secondaryText}
            </div>
          )}
        </>
      )}
      
      {isLongPressing && !disableLongPress && (
        <div className="long-press-indicator" aria-live="polite" style={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '10px',
          color: '#FFD700',
          fontWeight: 'bold',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          animation: 'pulse 0.5s ease-in-out infinite alternate'
        }}>
          Hold to select...
        </div>
      )}
      
      {isSelected && !disableResize && (
        <div className="selection-handles">
          <div 
            className="handle handle-nw"
            onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
            tabIndex={component.tabIndex ?? 0}
            role="button"
            aria-label={component.accessibility?.ariaLabel || "Resize top left"}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResizeMouseDown(e, 'nw');
              }
            }}
          ></div>
          <div 
            className="handle handle-ne"
            onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
            tabIndex={component.tabIndex ?? 0}
            role="button"
            aria-label={component.accessibility?.ariaLabel || "Resize top right"}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResizeMouseDown(e, 'ne');
              }
            }}
          ></div>
          <div 
            className="handle handle-sw"
            onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
            tabIndex={component.tabIndex ?? 0}
            role="button"
            aria-label={component.accessibility?.ariaLabel || "Resize bottom left"}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResizeMouseDown(e, 'sw');
              }
            }}
          ></div>
          <div 
            className="handle handle-se"
            onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
            tabIndex={component.tabIndex ?? 0}
            role="button"
            aria-label={component.accessibility?.ariaLabel || "Resize bottom right"}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResizeMouseDown(e, 'se');
              }
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default DraggableComponent;